import type { IMediaError } from '|>/shared/media-error'

import videojs from 'video.js'
import { Events } from '|>/shared/events'
import { button, span } from '|>/shared/h'
import { formatCountdown } from '|>/shared/utils/date'
import { on, register } from '|>/shared/vjs'
import { BaseComponent } from '../base'

import './error-retry-button.css'

/**
 * "Try Again" button shown in the error overlay.
 *
 * Handles two modes:
 *   'csl'           — CSL (433) errors; fires Events.Player.ConcurrentStreamRetry on click;
 *                     applies a 30-second countdown after a failed retry attempt.
 *   'smart-restart' — SmartRestart errors; fires Events.Player.Retry on click;
 *                     reads cooldownMs from the SmartRestartError event payload.
 *
 * Label states:
 *   idle     → Icon + "Try again"
 *   counting → localized "Try again in {n} sec"  (button_try_again_in key, {1} = seconds)
 */
@register
export class ErrorRetryButton extends BaseComponent {
  declare labelEl?: HTMLElement
  declare iconEl?: HTMLElement
  // null is common playback error (not SmartRestartable and not CSL error)
  private mode: 'csl' | 'smart-restart' | null = null
  private countdown: number = 0
  private intervalId?: ReturnType<typeof setInterval>

  // CSL-specific: set to true when user clicks retry; cleared on playing or full reset.
  // Controls whether the next 433 error triggers a 30s countdown.
  private isRetrying: boolean = false

  constructor(player, options) {
    super(player, options)
    ;(this.el() as HTMLElement).style.display = 'none'
  }

  override createEl() {
    const el = button('.error-retry-button', {}, [
      (this.iconEl = span('.vjs-icon-placeholder')),
      (this.labelEl = span('.error-retry-label', this.localize('Try again'))),
    ])

    videojs.on(el, 'click', (e: MouseEvent) => {
      this.handleClick(e)
    })

    return el
  }

  /** Show for CSL (433) errors; on a failed retry escalate to 30s countdown. Hides for all other errors. */
  @on('error')
  handleError() {
    const error = this.player_.error() as IMediaError | null
    if (error?.originalCode !== 433) {
      this.fullReset()
      return
    }

    this.mode = 'csl'
    this.showWithCountdown(this.isRetrying ? 30_000 : 0)
  }

  /** Show for SmartRestart errors; cooldownMs from payload drives the countdown. */
  @on(Events.Player.SmartRestartError)
  handleSmartRestartError(data: Events.Player.SmartRestartError) {
    this.mode = 'smart-restart'
    this.showWithCountdown(data?.cooldownMs ?? 0)
  }

  /** Hide on load; isRetrying is preserved until playback actually starts. */
  @on(Events.Media.Load)
  handleLoad(source?: Events.Media.Load) {
    // A sourceless Media.Load (source cleared) is not a new load and must not hide
    // the retry button of an active error (e.g. CSL 433 whose URL gets blanked).
    if (!source?.src) return
    this.stopTimer()
    this.countdown = 0
    this.updateLabel()
    ;(this.el() as HTMLElement).style.display = 'none'
  }

  /** Playback resumed — clear isRetrying so the next 433 shows without a countdown. */
  @on('playing')
  handlePlaying() {
    this.isRetrying = false
  }

  /** Content switched — full state reset. */
  @on(Events.Media.ContentChanged)
  handleContentChanged() {
    this.fullReset()
  }

  override handleLanguagechange() {
    this.updateLabel()
  }

  override dispose() {
    this.stopTimer()
    super.dispose()
  }

  private handleClick(e: MouseEvent) {
    e?.preventDefault()
    e?.stopPropagation()
    if (this.countdown > 0) return
    if (this.mode === 'csl') {
      this.isRetrying = true
      this.player_.trigger(Events.Player.ConcurrentStreamRetry)
    } else if (this.mode === 'smart-restart') {
      this.player_.trigger(Events.Player.Retry)
    }
  }

  /** Show the button with an optional countdown initialized from milliseconds. */
  private showWithCountdown(ms: number) {
    this.countdown = Math.ceil(ms / 1000)
    this.stopTimer()
    this.startTimer()
    this.updateLabel()
    ;(this.el() as HTMLElement).style.display = 'flex'
  }

  private startTimer() {
    if (this.countdown <= 0) return
    this.intervalId = setInterval(() => {
      this.countdown = Math.max(0, this.countdown - 1)
      this.updateLabel()
      if (this.countdown <= 0) {
        this.stopTimer()
      }
    }, 1000)
  }

  private stopTimer() {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId)
      this.intervalId = undefined
    }
  }

  private updateLabel() {
    const el = this.el() as HTMLButtonElement
    if (!el || !this.labelEl) return
    if (this.countdown > 0) {
      this.labelEl.textContent = this.localize('button_try_again_in', [
        formatCountdown(this.countdown),
      ])
      el.setAttribute('disabled', 'true')
      if (this.iconEl) this.iconEl.style.display = 'none'
    } else {
      this.labelEl.textContent = this.localize('Try again')
      el.removeAttribute('disabled')
      if (this.iconEl) this.iconEl.style.display = ''
    }
  }

  private fullReset() {
    this.stopTimer()
    this.countdown = 0
    this.isRetrying = false
    this.mode = null
    this.updateLabel()
    ;(this.el() as HTMLElement).style.display = 'none'
  }
}
